import { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Search, ChevronLeft, ChevronRight, CheckCircle2, Clock, AlertCircle, Ban, Info, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useHermesCampaignJobs, useUpdateQueueJobContent } from '@/hooks/useHermesCampaigns';
import { useToast } from '@/hooks/use-toast';
import { SequenceEmailComposer } from '@/components/email/SequenceEmailComposer';
import {
  computeCampaignJobCounts,
  getJobDisplayStatus,
  parseSuppressionReason,
  suppressionReasonLabel,
} from '@/lib/email/job-status';
import { resolveCampaignTotals, describeCampaignWindow, type DurableCampaignCounters } from '@/lib/email/campaign-totals';
import { captureError } from '@/lib/sentry';

interface RecipientEmailPreviewProps {
  campaignId: string;
  /**
   * Durable counters from the campaign row. Totals come from here, never from surviving
   * queue rows, which are purged 7 days after sending (#859). Optional so the component
   * still renders without it.
   */
  campaign?: DurableCampaignCounters;
}

function getRecipientName(job: any): string {
  const data = job.recipient_data;
  if (!data) return job.recipient_email || 'Unknown';
  const first = data.first_name || '';
  const last = data.last_name || '';
  return (first + ' ' + last).trim() || job.recipient_email || 'Unknown';
}

function getStatusIcon(job: { status: string; error_message?: string | null }) {
  const display = getJobDisplayStatus(job);
  switch (display) {
    case 'sent':
      return <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />;
    case 'pending':
    case 'processing':
    case 'retrying':
      return <Clock className="h-3.5 w-3.5 text-amber-500" />;
    case 'skipped-suppressed':
      // Calmer than the destructive red — the recipient just unsubscribed/
      // bounced/complained; nothing actually broke on our side.
      return <Ban className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'failed':
      return <AlertCircle className="h-3.5 w-3.5 text-destructive" />;
    default:
      return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

/**
 * Safely renders sanitized HTML from rendered email bodies.
 * DOMPurify.sanitize() strips all dangerous content (scripts, event handlers, etc.)
 * before rendering. This is the standard approach for displaying email previews.
 */
function SanitizedHtml({ html }: { html: string }) {
  const sanitizedHtml = DOMPurify.sanitize(html);
  return (
    <div
      className="prose prose-sm max-w-none dark:prose-invert w-full break-words [&_img]:max-w-full [&_table]:max-w-full"
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
}

export function RecipientEmailPreview({ campaignId, campaign }: RecipientEmailPreviewProps) {
  const { data: jobs = [], isLoading, isError, isSuccess: jobsLoaded } = useHermesCampaignJobs(campaignId);
  const updateJobContent = useUpdateQueueJobContent();
  const { toast } = useToast();

  const [searchFilter, setSearchFilter] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');

  // Durable totals (#859): recipients/sent always come from the campaign row, never from
  // surviving queue rows, which are purged 7 days after sending and would otherwise
  // under-report as the window ages. The window itself is withheld until the query settles —
  // an in-flight or failed query also yields an empty `jobs`, and treating that as "purged"
  // would put a false claim on screen.
  // isSuccess, not !isLoading: a disabled query reports isLoading=false in react-query v5
  // while never having fetched, which would read as a settled empty window.
  const windowSettled = jobsLoaded;
  const totals = useMemo(
    () => resolveCampaignTotals(campaign ?? {}, windowSettled ? computeCampaignJobCounts(jobs) : null),
    [campaign, jobs, windowSettled],
  );
  const windowNotice = describeCampaignWindow(totals);

  const filteredJobs = useMemo(() => {
    if (!searchFilter) return jobs;
    const q = searchFilter.toLowerCase();
    return jobs.filter(job => {
      const name = getRecipientName(job).toLowerCase();
      const email = (job.recipient_email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [jobs, searchFilter]);

  const selectedJob = filteredJobs[selectedIndex] || null;

  const handleSelectJob = (index: number) => {
    setSelectedIndex(index);
    setIsEditing(false);
  };

  const handlePrev = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setIsEditing(false);
    }
  };

  const handleNext = () => {
    if (selectedIndex < filteredJobs.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setIsEditing(false);
    }
  };

  const handleStartEdit = () => {
    if (!selectedJob || selectedJob.status !== 'pending') return;
    setEditSubject(selectedJob.rendered_subject || '');
    setEditBody(selectedJob.rendered_body || '');
    setIsEditing(true);
  };

  const handleSaveEdit = async (subject: string, body: string) => {
    if (!selectedJob) return;
    try {
      await updateJobContent.mutateAsync({
        jobId: selectedJob.id,
        rendered_subject: subject,
        rendered_body: body,
      });
      setIsEditing(false);
      toast({ title: 'Saved', description: 'Email content updated.' });
    } catch (error) {
      captureError(error as Error, { feature: 'campaigns-recipient-email-preview-handle-save-edit' });
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save changes',
        variant: 'destructive',
      });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // #859: a failed query also leaves `jobs` empty. Without this branch the user is told
  // "No email jobs found ... jobs are created when the campaign is scheduled" — two false
  // statements — about a campaign that may have delivered hundreds of emails. Mirrors the
  // sibling state in HermesCampaignContacts.
  if (isError) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Couldn&rsquo;t load this campaign&rsquo;s emails. Try again in a moment.
      </div>
    );
  }

  if (jobs.length === 0) {
    // #859: the queue window is purged 7 days after sending. A campaign that actually
    // delivered mail must not read as "no email jobs found" — reserve that copy for
    // campaigns that never queued (e.g. drafts) or have no durable totals to fall back on.
    // describeCampaignWindow returns null unless it can say something true.
    if (campaign && windowNotice) {
      return (
        <div className="text-center py-12 text-muted-foreground">
          <p>{windowNotice}</p>
        </div>
      );
    }
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No email jobs found for this campaign.</p>
        <p className="text-sm">Jobs are created when the campaign is scheduled.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 h-[600px]">
      {/* Left: Recipient list */}
      <div className="flex flex-col border rounded-lg overflow-hidden">
        {/* Search */}
        <div className="p-2 border-b space-y-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Filter by name or email..."
              value={searchFilter}
              onChange={(e) => { setSearchFilter(e.target.value); setSelectedIndex(0); }}
              className="pl-8 h-8 text-sm"
            />
          </div>
          {/* #859: recipients may exceed the surviving queue window as rows age out. */}
          {windowNotice && <p className="text-xs text-muted-foreground px-0.5">{windowNotice}</p>}
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {filteredJobs.map((job, idx) => (
            <div
              key={job.id}
              className={`flex items-center gap-2 px-3 py-2 cursor-pointer border-b last:border-b-0 text-sm hover:bg-muted/50 ${
                idx === selectedIndex ? 'bg-muted' : ''
              }`}
              onClick={() => handleSelectJob(idx)}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{getRecipientName(job)}</div>
                <div className="text-xs text-muted-foreground truncate">{job.recipient_email}</div>
              </div>
              {getStatusIcon(job)}
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between px-3 py-2 border-t text-sm">
          <Button variant="ghost" size="sm" onClick={handlePrev} disabled={selectedIndex === 0} className="h-7 px-2">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-muted-foreground text-xs">
            {filteredJobs.length > 0 ? `${selectedIndex + 1} of ${filteredJobs.length}` : '0 of 0'}
          </span>
          <Button variant="ghost" size="sm" onClick={handleNext} disabled={selectedIndex >= filteredJobs.length - 1} className="h-7 px-2">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Right: Email preview */}
      <div className="border rounded-lg overflow-hidden flex flex-col">
        {selectedJob ? (
          isEditing ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Editing email for {getRecipientName(selectedJob)}</h3>
                <Button variant="ghost" size="sm" onClick={handleCancelEdit} className="h-7 px-2">
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <SequenceEmailComposer
                defaultSubject={editSubject}
                defaultBody={editBody}
                onSubjectChange={setEditSubject}
                onBodyChange={setEditBody}
                onSave={handleSaveEdit}
                saveLabel={updateJobContent.isPending ? 'Saving...' : 'Save Changes'}
                disabled={updateJobContent.isPending}
              />
            </div>
          ) : (
            <>
              {/* Email header */}
              <div className="p-4 border-b space-y-1 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">To:</span>
                    <span className="font-medium">{selectedJob.recipient_email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {(() => {
                      const display = getJobDisplayStatus(selectedJob);
                      if (display === 'skipped-suppressed') {
                        const reason = parseSuppressionReason(selectedJob.error_message);
                        return (
                          <>
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Ban className="h-3 w-3" />
                              Skipped ({suppressionReasonLabel(reason)})
                            </Badge>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    type="button"
                                    className="text-muted-foreground hover:text-foreground"
                                    aria-label="About this skipped send"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  {reason === 'unsubscribed'
                                    ? 'This recipient unsubscribed and could not receive the email. Contact them through another channel if needed.'
                                    : reason === 'bounced'
                                    ? 'This recipient’s address previously bounced and could not receive the email. Verify the address or contact them another way.'
                                    : reason === 'complained'
                                    ? 'This recipient marked a previous email as spam, so we did not send to them. Contact them through another channel if needed.'
                                    : 'This recipient is suppressed and could not receive the email. Contact them through another channel if needed.'}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        );
                      }
                      return (
                        <Badge
                          variant={
                            display === 'sent'
                              ? 'default'
                              : display === 'failed'
                              ? 'destructive'
                              : 'secondary'
                          }
                        >
                          {selectedJob.status}
                        </Badge>
                      );
                    })()}
                    {selectedJob.status === 'pending' && (
                      <Button variant="outline" size="sm" onClick={handleStartEdit} className="h-7 text-xs">
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit Email
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Subject:</span>{' '}
                  <span className="font-medium">{selectedJob.rendered_subject || '(no subject)'}</span>
                </div>
                {selectedJob.scheduled_send_time && (
                  <div className="text-xs text-muted-foreground">
                    Scheduled: {new Date(selectedJob.scheduled_send_time).toLocaleString()}
                  </div>
                )}
                {/* Render the raw error_message only for genuine failures.
                    Suppression rejections are surfaced via the badge + tooltip
                    above so we don't double-show the "Recipient email status
                    is …" string in a red error line. */}
                {selectedJob.status !== 'pending' &&
                  selectedJob.status !== 'sent' &&
                  selectedJob.error_message &&
                  getJobDisplayStatus(selectedJob) !== 'skipped-suppressed' && (
                    <div className="text-xs text-destructive">Error: {selectedJob.error_message}</div>
                  )}
              </div>

              {/* Email body */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-4">
                {selectedJob.rendered_body ? (
                  <SanitizedHtml html={selectedJob.rendered_body} />
                ) : (
                  <p className="text-sm text-muted-foreground">(No email body)</p>
                )}
              </div>
            </>
          )
        ) : (
          <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
            Select a recipient to preview their email
          </div>
        )}
      </div>
    </div>
  );
}
